import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const RegistrarTestKolb = () => {
  const navigate = useNavigate();

  const { id, testid } = useParams();
  const edit = Boolean(testid);

  const [aprendizId, setAprendizId] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState([]);

  const [activeTab, setActiveTab] = useState("test");
  const [paginaActual, setPaginaActual] = useState(0);

  const [loading, setLoading] = useState(false);
 

  const preguntasPorPagina = 2;
  const allOptions = useMemo(() => [1, 2, 3, 4], []);

  const blankRespuesta = useMemo(
    () => ({
      experienciaConcreta: "",
      observacionReflexiva: "",
      conceptualizacionAbstracta: "",
      experimentacionActiva: "",
    }),
    []
  );

  useEffect(() => {
    setAprendizId(id || null);
  }, [id]);

  const totalPaginas = useMemo(() => {
    return Math.ceil((preguntas?.length || 0) / preguntasPorPagina);
  }, [preguntas?.length, preguntasPorPagina]);

  const sumatoria = useCallback(
    (clave) =>
      respuestas.reduce((acc, r) => acc + (parseInt(r?.[clave], 10) || 0), 0),
    [respuestas]
  );

  const sumaEC = useMemo(() => sumatoria("experienciaConcreta"), [sumatoria]);
  const sumaOR = useMemo(() => sumatoria("observacionReflexiva"), [sumatoria]);
  const sumaCA = useMemo(() => sumatoria("conceptualizacionAbstracta"), [sumatoria]);
  const sumaEA = useMemo(() => sumatoria("experimentacionActiva"), [sumatoria]);

  const diferenciaCAEC = useMemo(() => sumaCA - sumaEC, [sumaCA, sumaEC]);
  const diferenciaEAOR = useMemo(() => sumaEA - sumaOR, [sumaEA, sumaOR]);

  const determinarEstilo = useCallback(() => {
    if (diferenciaCAEC >= 0 && diferenciaEAOR >= 0) return "Convergente";
    if (diferenciaCAEC < 0 && diferenciaEAOR >= 0) return "Asimilador";
    if (diferenciaCAEC < 0 && diferenciaEAOR < 0) return "Divergente";
    if (diferenciaCAEC >= 0 && diferenciaEAOR < 0) return "Acomodador";
    return "";
  }, [diferenciaCAEC, diferenciaEAOR]);

  const respondidas = useMemo(() => {
    return respuestas.filter((r) => r && Object.values(r).every((v) => v !== "")).length;
  }, [respuestas]);

  const progress = useMemo(() => {
    return preguntas.length ? Math.round((respondidas / preguntas.length) * 100) : 0;
  }, [respondidas, preguntas.length]);

  useEffect(() => {
    const controller = new AbortController();

    const init = async () => {
      setLoading(true);
      try {
        const reqPreg = axios.get("/api/testkolb/preguntas/listall", {
          signal: controller.signal,
        });

        const reqTest = testid
          ? axios.get(`/api/testkolb/listid/${testid}`, { signal: controller.signal })
          : Promise.resolve({ data: null });

        const [resPreg, resTest] = await Promise.all([reqPreg, reqTest]);

        const preguntasList = resPreg?.data?.preguntas || [];
        setPreguntas(preguntasList);

        const base = preguntasList.map(() => ({ ...blankRespuesta }));

        if (!testid) {
          setRespuestas(base);
          setPaginaActual(0);
          setActiveTab("test");
          return;
        }

        const test = resTest?.data?.test;

        const aprendizDesdeTest =
          test?.aprendizObjectId?._id ||
          test?.aprendizObjectId ||
          test?.aprendiz?._id ||
          test?.aprendiz ||
          test?.aprendizId ||
          null;

        setAprendizId((prev) => prev || aprendizDesdeTest);

        const respuestasBackend = Array.isArray(test?.respuestas)
          ? test.respuestas
          : Array.isArray(test?.respuestasKolb)
            ? test.respuestasKolb
            : Array.isArray(test?.detalleRespuestas)
              ? test.detalleRespuestas
              : [];

        const mapByPregunta = new Map();
        for (const r of respuestasBackend) {
          const pid = String(r.pregunta?._id || r.pregunta || r.preguntaId || "");
          if (!pid) continue;

          mapByPregunta.set(pid, {
            experienciaConcreta: String(r.experienciaConcreta ?? ""),
            observacionReflexiva: String(r.observacionReflexiva ?? ""),
            conceptualizacionAbstracta: String(r.conceptualizacionAbstracta ?? ""),
            experimentacionActiva: String(r.experimentacionActiva ?? ""),
          });
        }

        const alineadas = preguntasList.map(
          (p) => mapByPregunta.get(String(p._id)) || { ...blankRespuesta }
        );

        setRespuestas(alineadas);
        setPaginaActual(0);
        setActiveTab("test");
      } catch (error) {
        if (axios.isCancel?.(error)) return;

        console.error("Error inicializando Kolb:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar las preguntas o el test. Revisa tu API.",
        });
      } finally {
        setLoading(false);
      }
    };

    init();
    return () => controller.abort();
  }, [testid, blankRespuesta]);

  const handlePreguntaChange = (index, nuevosValores) => {
    setRespuestas((prev) => {
      const copy = [...prev];
      copy[index] = nuevosValores;
      return copy;
    });
  };

  const validarPagina = () => {
    const inicio = paginaActual * preguntasPorPagina;
    const fin = inicio + preguntasPorPagina;
    const slice = respuestas.slice(inicio, fin);

    if (!slice.length) return false;
    return slice.every((r) => Object.values(r).every((v) => v !== ""));
  };

  const buildPayload = () => ({
    aprendizObjectId: aprendizId,
    sumas: {
      experienciaConcreta: sumaEC,
      observacionReflexiva: sumaOR,
      conceptualizacionAbstracta: sumaCA,
      experimentacionActiva: sumaEA,
    },
    diferencias: {
      conceptualizacionVsExperiencia: diferenciaCAEC,
      experimentacionVsObservacion: diferenciaEAOR,
    },
    estiloAprendizaje: determinarEstilo(),
    respuestas: respuestas.map((r, i) => ({
      pregunta: preguntas[i]?._id,
      experienciaConcreta: Number(r.experienciaConcreta),
      observacionReflexiva: Number(r.observacionReflexiva),
      conceptualizacionAbstracta: Number(r.conceptualizacionAbstracta),
      experimentacionActiva: Number(r.experimentacionActiva),
    })),
  });

  const validarCompleto = () => {
    if (!aprendizId) {
      Swal.fire(
        "Falta aprendiz",
        "No se recibió el ID del aprendiz. Revisa la ruta o el test.",
        "warning"
      );
      return false;
    }
    if (!preguntas.length) {
      Swal.fire("Sin preguntas", "No hay preguntas disponibles.", "warning");
      return false;
    }
    const ok =
      respuestas.length === preguntas.length &&
      respuestas.every((r) => r && Object.values(r).every((v) => v !== ""));
    if (!ok) {
      Swal.fire("Incompleto", "Debes responder todas las opciones.", "warning");
      return false;
    }
    return true;
  };

  const saveTestKolb = async () => {
    if (!validarCompleto()) return;
    setLoading(true);
    try {
      await axios.post("/api/testkolb/add", buildPayload());

      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "El test fue guardado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      navigate("/aprendiz/");
    } catch (error) {
      console.error("Error guardando test:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo guardar el test.",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateTestKolb = async () => {
    if (!validarCompleto()) return;
    setLoading(true);
    try {
      await axios.put(`/api/testkolb/update/${testid}`, buildPayload());

      Swal.fire({
        icon: "success",
        title: "¡Actualizado!",
        text: "El test fue actualizado correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });

      navigate("/aprendiz/");
    } catch (error) {
      console.error("Error actualizando test:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo actualizar el test.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    edit ? await updateTestKolb() : await saveTestKolb();
  };

  // ✅ Pregunta con truncate RESPONSIVE
  const renderPregunta = (index, pregunta) => {
    const valores = respuestas[index] || { ...blankRespuesta };

    const getAvailableOptions = (currentKey) => {
      const usados = Object.entries(valores)
        .filter(([k, v]) => v !== "" && k !== currentKey)
        .map(([_, v]) => Number(v));
      return allOptions.filter((o) => !usados.includes(o));
    };

    const handleChange = (key, value) => {
      handlePreguntaChange(index, { ...valores, [key]: value });
    };

    const labels = {
      experienciaConcreta: "Experiencia Concreta",
      observacionReflexiva: "Observación Reflexiva",
      conceptualizacionAbstracta: "Conceptualización Abstracta",
      experimentacionActiva: "Experimentación Activa",
    };

    const keys = Object.keys(labels);

    return (
      <div className="row g-3">
        {keys.map((key) => (
          <div className="col-12 col-md-6 col-lg-3" key={key}>
            <div className="bg-white border rounded-4 p-3 h-100 d-flex flex-column">
              <label className="form-label fw-semibold mb-2">{labels[key]}</label>

              {/* ✅ Móvil: wrap | Desktop: truncate */}
              <div className="small text-secondary mb-3" style={{ minHeight: 48 }}>
                {/* móvil */}
                <div className="d-block d-md-none" title={pregunta?.[key]?.principal || ""}>
                  {pregunta?.[key]?.principal}
                </div>
                <div className="d-block d-md-none" title={pregunta?.[key]?.secundario || ""}>
                  {pregunta?.[key]?.secundario}
                </div>

                {/* md+ */}
                <div className="d-none d-md-block text-truncate" title={pregunta?.[key]?.principal || ""}>
                  {pregunta?.[key]?.principal}
                </div>
                <div className="d-none d-md-block text-truncate" title={pregunta?.[key]?.secundario || ""}>
                  {pregunta?.[key]?.secundario}
                </div>
              </div>

              <div className="mt-auto">
                <select
                  className="form-select"
                  value={valores[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  required
                >
                  <option value="">Seleccionar</option>
                  {getAvailableOptions(key).map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleCancel = () => navigate("/aprendiz/");

  const irResultados = () => {
    if (!validarCompleto()) return;
    setActiveTab("resultados");
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div className="flex-grow-1">
                <h5 className="mt-2 mb-1 fw-semibold">Test de Kolb</h5>
                <small className="text-secondary">
                  {edit ? "Edición del test" : "Registro del test"} · Aprendiz:{" "}
                  <span className="fw-semibold">{aprendizId || "—"}</span>
                </small>

                <div className="mt-2">
                  <div className="progress" style={{ height: 10 }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                  <small className="text-secondary">{progress}% completado</small>
                </div>
              </div>

              {/* ✅ Botones responsive: full en móvil */}
              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => window.history.back()}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>



                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleCancel}
                  disabled={loading}
                  title="Cancelar"
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            {/* ✅ Tabs responsive */}
            <ul className="nav nav-tabs nav-fill flex-column flex-sm-row mb-4">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "test" ? "active" : ""}`}
                  onClick={() => setActiveTab("test")}
                  disabled={loading}
                >
                  Test
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "resultados" ? "active" : ""}`}
                  onClick={irResultados}
                  disabled={loading}
                  title="Ver resultados (requiere completar)"
                >
                  Resultados
                </button>
              </li>
            </ul>

            <form id="form-kolb" onSubmit={handleSubmit} noValidate>
              {/* TAB TEST */}
              {activeTab === "test" && (
                <>
                  {!preguntas.length ? (
                    <div className="alert alert-warning mb-0">
                      <i className="fa-solid fa-triangle-exclamation me-2"></i>
                      No hay preguntas para mostrar.
                    </div>
                  ) : (
                    <>
                      {preguntas
                        .slice(
                          paginaActual * preguntasPorPagina,
                          (paginaActual + 1) * preguntasPorPagina
                        )
                        .map((pregunta, idx) =>
                          renderPregunta(paginaActual * preguntasPorPagina + idx, pregunta)
                        )}

                      {/* ✅ Footer paginación responsive */}
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100 w-md-auto"
                          onClick={() => setPaginaActual((p) => Math.max(0, p - 1))}
                          disabled={loading || paginaActual === 0}
                        >
                          <i className="fa-solid fa-arrow-left me-2"></i>
                          Anterior
                        </button>

                        <small className="text-secondary text-center order-2 order-md-0 w-100 w-md-auto">
                          Página <span className="fw-semibold">{paginaActual + 1}</span> de{" "}
                          <span className="fw-semibold">{totalPaginas}</span>
                        </small>

                        {paginaActual < totalPaginas - 1 ? (
                          <button
                            type="button"
                            className="btn btn-primary w-100 w-md-auto"
                            onClick={() => validarPagina() && setPaginaActual((p) => p + 1)}
                            disabled={loading || !validarPagina()}
                          >
                            Siguiente
                            <i className="fa-solid fa-arrow-right ms-2"></i>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-dark w-100 w-md-auto"
                            onClick={irResultados}
                            disabled={loading || !validarPagina()}
                            title="Ir a resultados"
                          >
                            Ver Resultados
                            <i className="fa-solid fa-chart-simple ms-2"></i>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* TAB RESULTADOS */}
              {activeTab === "resultados" && (
                <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div>
                      <h6 className="fw-semibold mb-1">Resultados Finales</h6>
                      <small className="text-secondary">Verifica tus sumas antes de guardar.</small>
                    </div>
                    <span className="badge text-bg-light border">
                      Estilo: <span className="fw-semibold">{determinarEstilo()}</span>
                    </span>
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Suma EC</label>
                      <input className="form-control" value={sumaEC} disabled />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Suma OR</label>
                      <input className="form-control" value={sumaOR} disabled />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Suma CA</label>
                      <input className="form-control" value={sumaCA} disabled />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Suma EA</label>
                      <input className="form-control" value={sumaEA} disabled />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Diferencia CA - EC</label>
                      <input className="form-control" value={diferenciaCAEC} disabled />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Diferencia EA - OR</label>
                      <input className="form-control" value={diferenciaEAOR} disabled />
                    </div>

                    <div className="col-12">
                      <div className="alert alert-info mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        Estilo de aprendizaje calculado:{" "}
                        <span className="fw-semibold">{determinarEstilo()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer buttons */}
              <div className="col-12 pt-4">
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setActiveTab("test")}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-arrow-left me-2"></i>
                    Volver al Test
                  </button>

                  <button
                    type="submit"
                    form="form-kolb"
                    className="btn btn-dark"
                    disabled={loading}
                    title={edit ? "Actualizar Test" : "Guardar Test"}
                  >
                    <i
                      className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                        } me-2`}
                    ></i>
                    {edit ? "Actualizar Test" : "Guardar Test"}
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RegistrarTestKolb;